import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { reviewApi, languagesApi, vocabularyApi, lexiconApi } from '../utils/api';
import { getWordColor, formatStrength } from '../utils/strengthUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import RecallPrompt from '../components/RecallPrompt';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Sparkles,
  Frown,
  ArrowRight,
  Trophy,
  Loader2,
  Volume2,
  Edit3,
  Plus,
  Bookmark,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWordPopover } from '../shared/reader/useWordPopover';
import WordPopover from '../shared/reader/WordPopover';

export default function Review() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(() => searchParams.get('language_id') || '__mix__');
  const [sessionSize, setSessionSize] = useState(20);
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sessionStats, setSessionStats] = useState({
    correct: 0,
    incorrect: 0,
    total: 0
  });

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        // Get only user's languages, not all languages
        const res = await languagesApi.getUserLanguages();
        const raw = Array.isArray(res.data) ? res.data : (res.data.languages || []);
        // Normalize: each entry is { language_id, language: { code, name, ... } }
        const normalized = raw
          .map((entry) => {
            const inner = entry.language || {};
            return {
              language_id: entry.language_id || inner.language_id,
              code: inner.code || entry.code,
              name: inner.name || entry.name,
              native_name: inner.native_name || entry.native_name,
              is_primary: entry.is_primary,
            };
          })
          .filter((l) => l.language_id && l.name);
        setLanguages(normalized);
      } catch (err) {
        console.error('Error fetching languages:', err);
      }
    };

    fetchLanguages();
  }, []);

  useEffect(() => {
    const fetchQueue = async () => {
      setLoading(true);
      try {
        const isMix = selectedLanguage === '__mix__';
        const params = {
          limit: sessionSize,
          ...(isMix
            ? { all_languages: true }
            : selectedLanguage
            ? { language_id: selectedLanguage }
            : {}),
        };
        const res = await reviewApi.getQueue(params);
        setQueue(res.data.items);
        setCurrentIndex(0);
        setShowAnswer(false);
        setSessionStats({ correct: 0, incorrect: 0, total: 0 });
      } catch (err) {
        console.error('Error fetching review queue:', err);
        toast.error('Failed to load review queue');
      } finally {
        setLoading(false);
      }
    };

    fetchQueue();
  }, [selectedLanguage, sessionSize]);

  const currentItem = queue[currentIndex];
  const isComplete = currentIndex >= queue.length || queue.length === 0;

  // Edit popover state — uses the shared useWordPopover hook
  const reviewLangCode = currentItem?.language_code || currentItem?.lexeme?.language_code || languages.find(l => l.language_id === selectedLanguage)?.code || '';
  const popover = useWordPopover({ languageCode: reviewLangCode });
  const [popoverActionLoading, setPopoverActionLoading] = useState(false);
  const [oracleDialogOpen, setOracleDialogOpen] = useState(false);
  
  // Flashcard state
  const [flashcardModal, setFlashcardModal] = useState(false);
  const [flashcardWord, setFlashcardWord] = useState(null);
  const [flashcardLists, setFlashcardLists] = useState([]);
  const [selectedFlashcardList, setSelectedFlashcardList] = useState('');
  const [newListName, setNewListName] = useState('');
  const [savingFlashcard, setSavingFlashcard] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleEditWord = (item, event) => {
    const word = item?.lexeme?.normalized_form || item?.normalized_form || '';
    const langCode = item?.language_code || reviewLangCode;
    if (word && langCode) {
      const token = {
        token_id: `review-${item.state_id || Date.now()}`,
        normalized_form: word.toLowerCase(),
        surface_form: word,
        language_code: langCode,
        is_word: true,
      };
      popover.open(token, event);
      setOracleDialogOpen(true);
    }
  };

  // Called when the Oracle Dialog is closed — refresh the card's data from the API
  const handleOracleClose = async () => {
    setOracleDialogOpen(false);
    popover.close();
    // Re-fetch word data to pick up any changes made in the Oracle
    const word = currentItem?.lexeme?.normalized_form || currentItem?.normalized_form || '';
    const langCode = currentItem?.language_code || reviewLangCode;
    if (!word || !langCode) return;
    try {
      const res = await vocabularyApi.getWordData(word, langCode);
      const fresh = res.data;
      if (!fresh) return;
      const newTranslation = fresh.user_state?.translation || fresh.translation || currentItem.translation;
      const newNote = fresh.user_state?.note || currentItem.note;
      const newStrength = fresh.user_state?.strength ?? currentItem.strength;
      setQueue(prev => prev.map((item, i) =>
        i === currentIndex
          ? { ...item, translation: newTranslation, note: newNote, strength: newStrength }
          : item
      ));
    } catch { /* silent — card will refresh on next load */ }
  };

  // Mark word as ignored and skip it from the current session
  const handleDeleteCard = async (item) => {
    const word = item?.lexeme?.normalized_form || item?.normalized_form || '';
    const langCode = item?.language_code || reviewLangCode;
    if (!word || !langCode) return;
    try {
      await vocabularyApi.setWordStatus(word, langCode, 'ignored');
      toast.success(`"${word}" removed from reviews`);
    } catch { toast.error('Could not remove word'); }
    // Skip past this card regardless of API outcome
    setQueue(prev => prev.filter((_, i) => i !== currentIndex));
    setShowAnswer(false);
  };

  const handlePopoverSetStatus = async (status) => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setPopoverActionLoading(true);
    try {
      await vocabularyApi.setWordStatus(term, reviewLangCode, status);
      toast.success(`Marked as ${status}`);
      popover.close();
    } catch { toast.error('Could not update status'); }
    finally { setPopoverActionLoading(false); }
  };

  const handlePopoverSaveTranslation = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setPopoverActionLoading(true);
    try {
      let stateId = popover.wordData?.user_state?.state_id;
      if (!stateId) {
        await vocabularyApi.setWordStatus(term, reviewLangCode, 'new');
        const res = await vocabularyApi.getWordData(term, reviewLangCode);
        stateId = res.data?.user_state?.state_id;
      }
      if (stateId) {
        await vocabularyApi.addTranslation(stateId, popover.translation);
        popover.setTranslationSaved(true);
        toast.success('Translation saved');
      }
    } catch { toast.error('Could not save translation'); }
    finally { setPopoverActionLoading(false); }
  };

  const handlePopoverSaveNote = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setPopoverActionLoading(true);
    try {
      await vocabularyApi.saveUserNote(term, reviewLangCode, popover.note);
      toast.success('Note saved');
    } catch { toast.error('Could not save note'); }
    finally { setPopoverActionLoading(false); }
  };

  const handlePopoverAutoTranslate = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setPopoverActionLoading(true);
    try {
      const res = await vocabularyApi.enrichWord(term, reviewLangCode, 'translation', true);
      const tr = res.data?.data?.translation || res.data?.translation;
      if (tr) { popover.setTranslation(tr); popover.setTranslationSaved(false); }
    } catch { toast.error('Translation failed'); }
    finally { setPopoverActionLoading(false); }
  };

  const handlePopoverRefreshSection = async (section) => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setPopoverActionLoading(true);
    try {
      const res = await vocabularyApi.enrichWord(term, reviewLangCode, section, true);
      if (res.data?.data) popover.setWordData(prev => prev ? { ...prev, ...res.data.data } : res.data.data);
    } catch { toast.error('Refresh failed'); }
    finally { setPopoverActionLoading(false); }
  };

  const handlePopoverToggleFieldLock = async (field) => {
    const term = popover.selectedToken?.normalized_form || popover.wordData?.word;
    if (!term || !reviewLangCode) return;
    setPopoverActionLoading(true);
    try {
      const res = await lexiconApi.toggleFieldLock(term, reviewLangCode, field);
      const newLocked = res.data?.locked_fields || [];
      popover.setWordData(prev => prev ? { ...prev, locked_fields: newLocked } : prev);
      toast.success(`${field} ${res.data?.action || 'toggled'}`);
    } catch { toast.error('Failed to update field lock'); }
    finally { setPopoverActionLoading(false); }
  };

  const handlePopoverPlayAudio = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    try {
      const audio = new Audio(`${BACKEND_URL}/api/tts/word?word=${encodeURIComponent(term)}&language=${reviewLangCode}`);
      await audio.play();
    } catch { toast.error('Audio unavailable'); }
  };

  const handleAddFlashcard = async (item) => {
    setFlashcardWord(item);
    setNewListName('');
    setSelectedFlashcardList('');
    setFlashcardModal(true);
    try {
      const res = await vocabularyApi.getFlashcardLists();
      setFlashcardLists(res.data || []);
    } catch {
      setFlashcardLists([]);
    }
  };

  const confirmAddFlashcard = async () => {
    if (!flashcardWord) {
      toast.error('No word selected');
      return;
    }
    if (!selectedFlashcardList && !newListName.trim()) {
      toast.error('Select an existing list or type a new list name');
      return;
    }
    setSavingFlashcard(true);
    try {
      let listId = selectedFlashcardList;
      if (newListName.trim()) {
        const res = await vocabularyApi.createFlashcardList({
          name: newListName.trim(),
          language_code: flashcardWord.language_code,
        });
        listId = res.data?.list_id || res.data?._id;
      }
      if (!listId) {
        toast.error('Could not resolve list — please try again');
        return;
      }
      const wordValue = flashcardWord.normalized_form
        || flashcardWord.lexeme?.normalized_form
        || flashcardWord.surface_form;
      await vocabularyApi.addToFlashcardList(listId, {
        word: wordValue,
        language_code: flashcardWord.language_code,
        definitions: flashcardWord.lexeme?.definitions || [],
      });
      toast.success(`"${wordValue}" added to flashcard list`);
      setFlashcardModal(false);
      setNewListName('');
      setSelectedFlashcardList('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add to flashcard list');
    } finally {
      setSavingFlashcard(false);
    }
  };

  const handleAnswer = useCallback(async (outcome) => {
    if (!currentItem || submitting) return;

    setSubmitting(true);
    const startedAt = Date.now();

    // Helper: one attempt + an automatic retry on network blips so a flaky
    // moment can't corrupt your session.
    const tryOnce = () =>
      reviewApi.submitAnswer({
        state_id: currentItem.state_id,
        normalized_form: currentItem.normalized_form,
        language_code: currentItem.language_code,
        language_id: currentItem.language_id,
        outcome,
        response_time_ms: Date.now() - startedAt,
      });

    let res;
    try {
      try {
        res = await tryOnce();
      } catch (err) {
        // One automatic retry after a short backoff
        await new Promise((r) => setTimeout(r, 600));
        res = await tryOnce();
      }

      // Backend returns 200 with { ok: false, error, message } on logical
      // failures so we can show a clean message instead of a generic toast.
      if (res?.data && res.data.ok === false) {
        toast.error(res.data.message || 'Could not save your answer. Try again.');
        setSubmitting(false);
        return;
      }

      // Update stats
      const isCorrect = outcome === 'correct' || outcome === 'easy';
      setSessionStats((prev) => ({
        correct: prev.correct + (isCorrect ? 1 : 0),
        incorrect: prev.incorrect + (isCorrect ? 0 : 1),
        total: prev.total + 1,
      }));

      // Move to next
      setCurrentIndex((prev) => prev + 1);
      setShowAnswer(false);
    } catch (err) {
      console.error('Error submitting answer:', err);
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      toast.error(detail ? `Could not save: ${detail}` : 'Failed to submit answer. Check your connection.');
    } finally {
      setSubmitting(false);
    }
  }, [currentItem, queue]);

  // Keyboard: Enter/Return → show answer or continue; 1-4 → answer buttons
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (oracleDialogOpen) return;  // Oracle has its own keyboard handling
      if (e.key === 'Enter' || e.key === 'Return') {
        e.preventDefault();
        if (!showAnswer && !isComplete && currentItem) {
          setShowAnswer(true);
        }
      }
      if (showAnswer && !isComplete && currentItem) {
        const keyMap = { '1': 'incorrect', '2': 'hard', '3': 'correct', '4': 'easy' };
        if (keyMap[e.key]) {
          e.preventDefault();
          handleAnswer(keyMap[e.key]);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAnswer, isComplete, currentItem, handleAnswer, oracleDialogOpen]);

  if (loading) {
    return <ReviewSkeleton />;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('review.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('review.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Language / mix selector */}
          <Select
            value={selectedLanguage || "__mix__"}
            onValueChange={(value) => setSelectedLanguage(value)}
          >
            <SelectTrigger className="w-52" data-testid="language-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__mix__">Stale Mix — all languages</SelectItem>
              {languages.map(lang => (
                <SelectItem key={lang.language_id} value={lang.language_id}>
                  {lang.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Session size selector */}
          <Select
            value={String(sessionSize)}
            onValueChange={(v) => setSessionSize(Number(v))}
          >
            <SelectTrigger className="w-36" data-testid="session-size-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 30, 50].map(n => (
                <SelectItem key={n} value={String(n)}>{n} words</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Progress */}
      {queue.length > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{t('review.progress')}</span>
            <span>{Math.min(currentIndex, queue.length)} / {queue.length}</span>
          </div>
          <Progress value={(currentIndex / queue.length) * 100} className="h-2" />
        </div>
      )}

      {/* Review Card */}
      {isComplete ? (
        <ReviewComplete 
          stats={sessionStats} 
          onRestart={() => {
            setCurrentIndex(0);
            setShowAnswer(false);
            setSessionStats({ correct: 0, incorrect: 0, total: 0 });
          }}
          onGoHome={() => navigate('/dashboard')}
          hasItems={queue.length > 0}
        />
      ) : (
        <>
          <QueueBarGraph queue={queue} currentIndex={currentIndex} />
          <ReviewCard
            item={currentItem}
            showAnswer={showAnswer}
            onShowAnswer={() => setShowAnswer(true)}
            onAnswer={handleAnswer}
            submitting={submitting}
            onEdit={(e) => handleEditWord(currentItem, e)}
            onAddFlashcard={() => handleAddFlashcard(currentItem)}
            onDelete={() => handleDeleteCard(currentItem)}
          />
        </>
      )}

      {/* Session Stats */}
      {!isComplete && sessionStats.total > 0 && (
        <div className="flex justify-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-green-500">
            <CheckCircle className="h-4 w-4" />
            {sessionStats.correct} {t('review.correct')}
          </div>
          <div className="flex items-center gap-2 text-red-500">
            <XCircle className="h-4 w-4" />
            {sessionStats.incorrect} {t('review.incorrect')}
          </div>
        </div>
      )}

      {/* Flashcard List Selection Modal */}
      <Dialog open={flashcardModal} onOpenChange={setFlashcardModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="h-5 w-5" />
              Add to Flashcard List
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Save <strong>{flashcardWord?.normalized_form || flashcardWord?.lexeme?.normalized_form}</strong> to a flashcard list:
            </p>
            {flashcardLists.length > 0 && (
              <Select value={selectedFlashcardList} onValueChange={setSelectedFlashcardList}>
                <SelectTrigger>
                  <SelectValue placeholder="Select existing list..." />
                </SelectTrigger>
                <SelectContent>
                  {flashcardLists.map(list => (
                    <SelectItem key={list.list_id || list._id} value={list.list_id || list._id}>
                      {list.name} ({list.word_count || 0} words)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Or create:</span>
              <Input
                placeholder="New list name..."
                value={newListName}
                onChange={(e) => { setNewListName(e.target.value); setSelectedFlashcardList(''); }}
                className="text-sm"
              />
            </div>
            <Button onClick={confirmAddFlashcard} disabled={savingFlashcard} className="w-full" data-testid="flashcard-confirm-btn">
              {savingFlashcard ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Flashcard
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Oracle Dialog — full Oracle panel in a modal */}
      <Dialog open={oracleDialogOpen} onOpenChange={(open) => { if (!open) handleOracleClose(); }}>
        <DialogContent className="p-0 overflow-hidden max-w-sm w-full" style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}>
          {popover.popoverPos && (
            <WordPopover
              style={{}}
              isModal={true}
              wordData={popover.wordData}
              wordLoading={popover.wordLoading}
              token={popover.selectedToken}
              language={{ code: reviewLangCode }}
              noteText={popover.note}
              setNoteText={popover.setNote}
              translationText={popover.translation}
              setTranslationText={popover.setTranslation}
              translationSaved={popover.translationSaved}
              onPlayAudio={handlePopoverPlayAudio}
              onSetStatus={handlePopoverSetStatus}
              onRelatedWordClick={(term) => popover.openForTerm(term)}
              onPopoverBack={() => popover.back()}
              historyDepth={popover.historyDepth}
              onSaveTranslation={handlePopoverSaveTranslation}
              onAutoTranslate={handlePopoverAutoTranslate}
              onSaveNote={handlePopoverSaveNote}
              onRefreshSection={handlePopoverRefreshSection}
              onToggleFieldLock={handlePopoverToggleFieldLock}
              onClose={handleOracleClose}
              actionLoading={popoverActionLoading}
              locked={popover.wordData?.locked}
              lockedFields={popover.wordData?.locked_fields || []}
              isMobile={false}
              isAdmin={user?.is_admin}
              isExpression={popover.isExpression}
              hasCachedAudio={popover.hasCachedAudio}
              isNewEntry={popover.isNewEntry}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Legacy floating popover removed — Oracle now opens in a Dialog */}
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function QueueBarGraph({ queue, currentIndex }) {
  const remaining = queue.slice(currentIndex);
  if (remaining.length === 0) return null;

  // Count remaining per language
  const counts = {};
  remaining.forEach(item => {
    const lang = item.language_code || '?';
    counts[lang] = (counts[lang] || 0) + 1;
  });
  const bars = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const maxCount = Math.max(...bars.map(([, c]) => c), 1);

  return (
    <div className="flex items-end gap-2 px-1" style={{ height: 48 }} data-testid="queue-bar-graph">
      {bars.map(([lang, count]) => {
        const pct = (count / maxCount) * 100;
        return (
          <div key={lang} className="flex flex-col items-center flex-1 min-w-0 h-full justify-end">
            <span className="text-[9px] text-muted-foreground mb-0.5 tabular-nums leading-none">{count}</span>
            <div
              className="w-full rounded-t-sm bg-violet-500/70 transition-all duration-300"
              style={{ height: `${Math.max(pct, 8)}%` }}
              title={`${lang}: ${count} remaining`}
            />
            <span className="text-[9px] text-muted-foreground mt-0.5 truncate w-full text-center leading-none uppercase">{lang}</span>
          </div>
        );
      })}
    </div>
  );
}

function ReviewCard({ item, showAnswer, onShowAnswer, onAnswer, submitting, onEdit, onAddFlashcard, onDelete }) {
  const { t } = useI18n();
  const strength = item?.strength || 0;
  const color = getWordColor(strength);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // Recall state: when user picks 'again' or 'hard', we show the RecallPrompt
  // before actually submitting the answer to the backend.
  const [pendingOutcome, setPendingOutcome] = useState(null);
  // Extra definitions fetched lazily when the queue item lacks them.
  const [extraDefs, setExtraDefs] = useState(null);
  const [extraReading, setExtraReading] = useState(null);

  // Reset lazy-fetched data when the card changes
  useEffect(() => {
    setExtraDefs(null);
    setExtraReading(null);
    setDeleteConfirm(false);
  }, [item?.state_id]);

  const word = item?.lexeme?.normalized_form
    || item?.lexeme?.lemma
    || item?.lexeme?.word
    || item?.lexeme?.normalized
    || item?.surface_form
    || item?.normalized_form
    || '';
  const langCode = item?.language_code || item?.lexeme?.language_code || '';

  const playAudio = () => {
    if (playing) return;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(true);
    const url = `${BACKEND_URL}/api/tts/speak?text=${encodeURIComponent(word)}&lang=${langCode}`;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.onerror = () => { setPlaying(false); console.log('TTS failed'); };
    audio.play().catch(() => setPlaying(false));
  };

  // Auto-play audio when card appears
  useEffect(() => {
    if (word && langCode) {
      const timer = setTimeout(playAudio, 300);
      return () => clearTimeout(timer);
    }
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, [word]);

  // Gather enrichment data (merge in any lazily-fetched extras)
  const ipa = item?.ipa || item?.locked_data?.ipa || item?.lexeme?.ipa;
  const ipaVariants = item?.ipa_variants || item?.locked_data?.ipa_variants || item?.lexeme?.ipa_variants;
  const zhuyin = item?.zhuyin || item?.locked_data?.zhuyin || item?.lexeme?.zhuyin;
  const jyutping = item?.jyutping || item?.locked_data?.jyutping || item?.lexeme?.jyutping;
  const romaji = item?.romaji || item?.locked_data?.romaji || item?.lexeme?.romaji;
  const reading = item?.reading || item?.locked_data?.pinyin || item?.locked_data?.reading || item?.lexeme?.pinyin || item?.lexeme?.reading || item?.lexeme?.transliteration || extraReading;
  const examples = item?.examples || item?.locked_data?.examples || item?.lexeme?.examples || [];

  // Get definitions
  const baseDefs = item?.locked_data?.definitions?.length > 0
    ? item.locked_data.definitions
    : item?.lexeme?.definitions?.length > 0
      ? item.lexeme.definitions
      : item?.lexeme?.definition
        ? [{ pos: item.lexeme.part_of_speech || '', definition: item.lexeme.definition }]
        : [];
  const defs = baseDefs.length > 0 ? baseDefs : (extraDefs || []);

  // Lazy enrichment: if this card has no definition, fetch it on-demand.
  // We only trigger this once per card (keyed on state_id).
  useEffect(() => {
    if (!word || !langCode) return;
    if (baseDefs.length > 0) return; // already have defs
    if (extraDefs) return;           // already fetched
    let cancelled = false;
    (async () => {
      try {
        const res = await vocabularyApi.enrichWord(word, langCode, null, false);
        const data = res.data?.data || {};
        if (cancelled) return;
        if (Array.isArray(data.definitions) && data.definitions.length > 0) {
          setExtraDefs(data.definitions);
        } else {
          setExtraDefs([]);
        }
        if (!reading && (data.pinyin || data.reading || data.transliteration)) {
          setExtraReading(data.pinyin || data.reading || data.transliteration);
        }
      } catch (err) {
        setExtraDefs([]); // mark as attempted to avoid retry loop
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.state_id]);

  // Intercept "again"/"hard" to show recall prompt first
  const handleAnswerClick = (outcome) => {
    if (outcome === 'incorrect' || outcome === 'hard') {
      setPendingOutcome(outcome);
    } else {
      onAnswer(outcome);
    }
  };

  const handleRecallContinue = () => {
    const out = pendingOutcome;
    setPendingOutcome(null);
    onAnswer(out);
  };

  return (
    <Card className="card-hover" data-testid="review-card">
      <CardContent className="p-8 text-center">
        {/* Word + Audio */}
        <div className="py-6">
          {/* Source language indicator (#32) */}
          {langCode && (
            <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider" data-testid="review-language-label">
              {langCode}
            </p>
          )}
          <div className="flex items-center justify-center gap-3">
            <h2 
              className="text-4xl md:text-5xl font-bold font-reader"
              style={{ color }}
              data-testid="review-word"
            >
              {word}
            </h2>
            <button
              onClick={playAudio}
              disabled={playing}
              className={`p-2 rounded-full transition-all ${playing ? 'bg-primary/20 text-primary animate-pulse' : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-primary'}`}
              data-testid="review-audio-btn"
            >
              <Volume2 className="h-5 w-5" />
            </button>
          </div>
          {item?.lexeme?.part_of_speech && (
            <Badge variant="secondary" className="mt-3">
              {item.lexeme.part_of_speech}
            </Badge>
          )}
        </div>

        {/* Answer Section */}
        {pendingOutcome ? (
          <RecallPrompt
            word={word}
            languageCode={langCode}
            definitions={defs}
            correctPinyin={reading || ''}
            onContinue={handleRecallContinue}
          />
        ) : showAnswer ? (
          <div className="space-y-4 animate-fade-in">
            {/* Reading / Pinyin / IPA */}
            {(reading || ipa) && (
              <>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {reading && (
                    <span className="text-lg font-mono text-primary" data-testid="review-reading">
                      {reading}
                    </span>
                  )}
                  {ipa && (
                    <span className="text-base font-mono text-muted-foreground" data-testid="review-ipa">
                      /{ipa}/
                    </span>
                  )}
                  {zhuyin && <span className="text-xs text-cyan-400 ml-1">{zhuyin}</span>}
                  {jyutping && <span className="text-xs text-amber-400 ml-1">粵 {jyutping}</span>}
                  {romaji && <span className="text-xs text-pink-400 ml-1">{romaji}</span>}
                </div>
                {Array.isArray(ipaVariants) && ipaVariants.length > 0 && (
                  <div className="flex justify-center flex-wrap gap-x-3 text-xs text-muted-foreground -mt-1">
                    {ipaVariants.map((v, i) => (
                      <span key={i}>{v.dialect}: <span className="text-foreground">/{v.ipa}/</span></span>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Definitions */}
            {defs.length > 0 && (
              <div className="p-4 rounded-lg bg-muted/50 text-left">
                <p className="text-sm text-muted-foreground mb-2">{t('review.definitions')}</p>
                {defs.slice(0, 3).map((def, idx) => (
                  <div key={idx} className="mb-2 last:mb-0">
                    {def.pos && <span className="text-xs font-medium text-primary mr-2">[{def.pos}]</span>}
                    <span className="text-base">{def.definition || def}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Translation */}
            {item?.translation && (
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm text-muted-foreground mb-1">{t('review.translation')}</p>
                <p className="text-lg font-semibold" data-testid="review-translation">{item.translation}</p>
              </div>
            )}

            {/* Example Sentences */}
            {examples.length > 0 && (
              <div className="p-4 rounded-lg bg-muted/30 border border-muted text-left">
                <p className="text-sm text-muted-foreground mb-2">Example</p>
                {examples.slice(0, 2).map((ex, idx) => (
                  <p key={idx} className="text-sm italic text-foreground/80 mb-1.5 last:mb-0" data-testid={`review-example-${idx}`}>
                    {typeof ex === 'string' ? ex : ex?.sentence || ex?.text || JSON.stringify(ex)}
                  </p>
                ))}
              </div>
            )}

            {/* Note/Gloss */}
            {(item?.note || item?.lexeme?.gloss) && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-base">
                  {item.note || item.lexeme.gloss}
                </p>
              </div>
            )}

            {/* Current Strength */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('review.currentStrength')}</p>
              <Progress value={strength * 100} className="h-2" />
              <p className="text-sm font-medium" style={{ color }}>
                {formatStrength(strength)}
              </p>
            </div>

            {/* Edit, Flashcard & Delete Buttons */}
            <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-xs text-muted-foreground hover:text-primary"
                onClick={(e) => onEdit?.(e)}
                data-testid="review-edit-btn"
              >
                <Edit3 className="h-3.5 w-3.5" />
                Edit entry
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-xs text-muted-foreground hover:text-amber-500"
                onClick={() => onAddFlashcard?.()}
                data-testid="review-flashcard-btn"
              >
                <Bookmark className="h-3.5 w-3.5" />
                Add to Flashcard List
              </Button>
              {deleteConfirm ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Ignore &amp; remove?</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1 text-xs text-red-500 hover:text-red-600 hover:bg-red-500/10 px-2"
                    onClick={() => { setDeleteConfirm(false); onDelete?.(); }}
                    data-testid="review-delete-confirm-btn"
                  >
                    Yes
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground px-2"
                    onClick={() => setDeleteConfirm(false)}
                    data-testid="review-delete-cancel-btn"
                  >
                    No
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-xs text-muted-foreground hover:text-red-500"
                  onClick={() => setDeleteConfirm(true)}
                  data-testid="review-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              )}
            </div>

            {/* Answer Buttons — also bound to keys 1/2/3/4 */}
            <div className="grid grid-cols-4 gap-2">
              <Button
                variant="outline"
                className="flex-col h-auto py-3 border-red-500/50 hover:bg-red-500/10"
                onClick={() => handleAnswerClick('incorrect')}
                disabled={submitting}
                data-testid="answer-incorrect"
              >
                <XCircle className="h-5 w-5 text-red-500 mb-1" />
                <span className="text-xs">{t('review.again')}</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">1</span>
              </Button>
              <Button
                variant="outline"
                className="flex-col h-auto py-3 border-orange-500/50 hover:bg-orange-500/10"
                onClick={() => handleAnswerClick('hard')}
                disabled={submitting}
                data-testid="answer-hard"
              >
                <Frown className="h-5 w-5 text-orange-500 mb-1" />
                <span className="text-xs">{t('review.hard')}</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">2</span>
              </Button>
              <Button
                variant="outline"
                className="flex-col h-auto py-3 border-green-500/50 hover:bg-green-500/10"
                onClick={() => handleAnswerClick('correct')}
                disabled={submitting}
                data-testid="answer-correct"
              >
                <CheckCircle className="h-5 w-5 text-green-500 mb-1" />
                <span className="text-xs">{t('review.good')}</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">3</span>
              </Button>
              <Button
                variant="outline"
                className="flex-col h-auto py-3 border-blue-500/50 hover:bg-blue-500/10"
                onClick={() => handleAnswerClick('easy')}
                disabled={submitting}
                data-testid="answer-easy"
              >
                <Sparkles className="h-5 w-5 text-blue-500 mb-1" />
                <span className="text-xs">{t('review.easy')}</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">4</span>
              </Button>
            </div>
          </div>
        ) : (
          <Button
            size="lg"
            onClick={onShowAnswer}
            className="gap-2"
            data-testid="show-answer-btn"
          >
            {t('review.showAnswer')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ReviewComplete({ stats, onRestart, onGoHome, hasItems }) {
  const { t } = useI18n();
  const accuracy = stats.total > 0 ? (stats.correct / stats.total * 100) : 0;

  if (!hasItems) {
    return (
      <Card className="text-center" data-testid="no-reviews-card">
        <CardContent className="py-12">
          <Trophy className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('review.allCaughtUp')}</h2>
          <p className="text-muted-foreground mb-6">
            {t('review.allCaughtUpDesc')}
          </p>
          <Button onClick={onGoHome} data-testid="go-dashboard-btn">
            {t('review.goToDashboard')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="text-center" data-testid="review-complete-card">
      <CardContent className="py-12">
        <Trophy className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
        <h2 className="text-2xl font-bold mb-2">{t('review.reviewComplete')}</h2>
        <p className="text-muted-foreground mb-6">
          {t('review.reviewCompleteDesc')}
        </p>
        
        <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto mb-8">
          <div>
            <p className="text-3xl font-bold text-green-500">{stats.correct}</p>
            <p className="text-sm text-muted-foreground">{t('review.correct')}</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-red-500">{stats.incorrect}</p>
            <p className="text-sm text-muted-foreground">{t('review.incorrect')}</p>
          </div>
          <div>
            <p className="text-3xl font-bold">{accuracy.toFixed(0)}%</p>
            <p className="text-sm text-muted-foreground">{t('review.accuracy')}</p>
          </div>
        </div>

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={onRestart} data-testid="restart-review-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('review.reviewAgain')}
          </Button>
          <Button onClick={onGoHome} data-testid="finish-review-btn">
            {t('review.done')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewSkeleton() {
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-4 w-48 mt-2" />
        </div>
        <Skeleton className="h-10 w-48" />
      </div>
      <Card>
        <CardContent className="p-8">
          <Skeleton className="h-16 w-3/4 mx-auto" />
          <Skeleton className="h-12 w-48 mx-auto mt-8" />
        </CardContent>
      </Card>
    </div>
  );
}
